/**
 * Step 3 of the Production Line journey (production-lines): the ranked
 * directions the analysis produced, one card each, the local selection and
 * the "Create my video" bar. Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import { useId, type ReactNode } from 'react';

import type { MerchantProfileView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { localizeMerchantFacingText } from '../../merchant/shared/merchantFacingText.js';
import {
  Button,
  Callout,
  CheckIcon,
  Eyebrow,
  Pix,
  SoonBadge,
  Tag,
  WarningIcon,
} from '../../merchant/ui/primitives.js';

type Recommendation = NonNullable<
  MerchantProfileView['result']
>['recommendations'][number];

export function RecommendationsStep({
  copy,
  language,
  workspaceName,
  view,
  selected,
  busy,
  onSelect,
  onCreate,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly view: MerchantProfileView;
  readonly selected: string | null;
  readonly busy: boolean;
  readonly onSelect: (id: string) => void;
  readonly onCreate: () => void;
}): ReactNode {
  const recommendations = view.result?.recommendations ?? [];
  const unavailable =
    view.result?.handoffArtifactId === null ||
    view.result?.handoffArtifactId === undefined;
  const chosen = recommendations.find((item) => item.artifactId === selected) ?? null;
  return (
    <section aria-labelledby="rc-h">
      <Eyebrow>
        <Pix status="ready-to-choose" />
        {copy.line.step3}
      </Eyebrow>
      <h2 id="rc-h" className="mj-ws-h2 mj-ws-mt-8">
        {copy.line.recsTitle(workspaceName)}
      </h2>
      <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4, maxWidth: '60ch' }}>
        {copy.line.recsBody}
      </p>
      <div className="mj-ws-row mj-ws-mt-12">
        <Tag>{copy.line.neutralTaste}</Tag>
        <SoonBadge />
      </div>
      {unavailable ? (
        <Callout className="mj-ws-mt-20">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">
            {copy.line.recsUnavailable}
          </span>
        </Callout>
      ) : (
        <div className="mj-ws-g3 mj-ws-mt-20" style={{ alignItems: 'stretch' }}>
          {recommendations.map((recommendation, index) => (
            <RecommendationCard
              key={recommendation.artifactId}
              recommendation={recommendation}
              recommended={index === 0}
              selected={recommendation.artifactId === selected}
              copy={copy}
              language={language}
              onSelect={() => {
                onSelect(recommendation.artifactId);
              }}
            />
          ))}
        </div>
      )}
      <div className="mj-ws-stickybar">
        <span className="mj-ws-muted mj-ws-sm">
          {chosen === null
            ? copy.line.selectHint
            : copy.line.selectedHint(chosen.title)}
        </span>
        <Button
          variant="primary"
          disabled={chosen === null || busy || unavailable}
          onClick={onCreate}
        >
          {busy ? copy.line.creating : copy.line.createVideo}
        </Button>
      </div>
    </section>
  );
}

function RecommendationCard({
  recommendation,
  recommended,
  selected,
  copy,
  language,
  onSelect,
}: {
  readonly recommendation: Recommendation;
  readonly recommended: boolean;
  readonly selected: boolean;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly onSelect: () => void;
}): ReactNode {
  const headingId = useId();
  const disabled = recommendation.povExecutability?.status === 'NOT_POV_SUITABLE';
  return (
    <article
      className={`mj-ws-rec${disabled ? ' is-disabled' : ''}`}
      aria-pressed={selected}
      aria-labelledby={headingId}
      onClick={(event) => {
        const target = event.target;
        if (
          !disabled &&
          target instanceof Element &&
          target.closest('button,a,summary,details') === null
        )
          onSelect();
      }}
    >
      <div className="mj-ws-rec-head">
        {recommended ? <Tag accent>{copy.line.recommended}</Tag> : null}
        <span className="mj-ws-check" aria-hidden="true">
          <CheckIcon />
        </span>
      </div>
      <h3 id={headingId} className="mj-ws-h3">
        {recommendation.title}
      </h3>
      <p className="mj-ws-sm">{recommendation.objective}</p>
      <dl className="mj-ws-rdl">
        <dt>{copy.line.angle}</dt>
        <dd>{recommendation.angle}</dd>
        <dt>{copy.line.audience}</dt>
        <dd>{recommendation.targetAudienceOrNeed.join(' · ')}</dd>
        <dt>{copy.line.proof}</dt>
        <dd>{recommendation.proofStructure}</dd>
        {recommendation.ctaDirection !== null ? (
          <>
            <dt>{copy.line.cta}</dt>
            <dd>{recommendation.ctaDirection}</dd>
          </>
        ) : null}
      </dl>
      <details className="mj-ws-details mj-ws-sm">
        <summary>{copy.line.whyFits}</summary>
        <p className="mj-ws-mt-8">
          {recommendation.whyItFits.join(' · ') || recommendation.rankingRationale}
        </p>
        {recommendation.limitations[0] !== undefined ? (
          <p className="mj-ws-muted mj-ws-mt-8">
            <strong>{copy.line.evidenceNote}</strong>{' '}
            {localizeMerchantFacingText(recommendation.limitations[0], language)}
          </p>
        ) : null}
      </details>
      {disabled ? (
        <p className="mj-ws-muted mj-ws-sm">
          <strong>{copy.line.notSuitable}</strong>{' '}
          {recommendation.povExecutability.reason}
        </p>
      ) : null}
      <Button
        variant={selected ? 'primary' : 'secondary'}
        small
        className="mj-ws-rec-action"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onSelect}
      >
        {selected ? copy.line.selected : copy.line.select}
      </Button>
    </article>
  );
}
